<script setup lang="ts">
import {
  type OrganisationMemoryCreate,
  type OrganisationMemoryInDb,
  OrganisationMemoryScope,
  type FilterableSearchQuery,
  createOrganisationMemory,
  deleteOrganisationMemory,
  searchOrganisationMemories,
  updateOrganisationMemory,
} from '@/api';
import {
  type DataTableColumns,
  type DataTableSortState,
  type FormInst,
  type FormRules,
  NButton,
  NIcon,
  NPopconfirm,
  NSpace,
  NTag,
} from 'naive-ui';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import type { FilterConfig } from '@/components/design/composite/GaiaFilterPanel.vue';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import { IconDelete, IconEdit } from '@/components/design/icons';
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { logger } from '@/utils/logger';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

const message = useGaiaToast();

const {
  currentPage,
  pageSize,
  searchQuery,
  sortKey,
  sortDirection,
  sortActivated,
  columnFilters,
  setColumnFilters,
} = useListQuerySync();

const scopeLabels: Record<OrganisationMemoryScope, string> = {
  [OrganisationMemoryScope.GENERAL]: 'General',
  [OrganisationMemoryScope.REVIEW]: 'Review',
  [OrganisationMemoryScope.PLAYBOOKS]: 'Playbooks',
  [OrganisationMemoryScope.CONTRACT_MANAGEMENT]: 'Contract Management',
  [OrganisationMemoryScope.CHAT]: 'Chat',
};

const scopeOptions = Object.entries(scopeLabels).map(([value, label]) => ({ label, value }));

// State
const memories = ref<OrganisationMemoryInDb[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const saving = ref<boolean>(false);

// Filter state
const filterOptions = ref<Record<string, (string | null)[]>>({});
const showFilterBar = ref<boolean>(false);
const filterConfig: FilterConfig[] = [{ key: 'scope', label: 'Scope' }];
const hasActiveFilters = computed(() => Object.keys(columnFilters.value).length > 0);

// Detail modal state
const showDetailModal = ref<boolean>(false);
const selectedMemory = ref<OrganisationMemoryInDb | null>(null);

// Create/edit modal state
const showFormModal = ref<boolean>(false);
const formRef = ref<FormInst | null>(null);
const editingMemory = ref<OrganisationMemoryInDb | null>(null);
const formData = ref<OrganisationMemoryCreate>(emptyForm());

function emptyForm(): OrganisationMemoryCreate {
  return {
    key: '',
    description: '',
    content: '',
    scope: OrganisationMemoryScope.GENERAL,
  };
}

const formRules: FormRules = {
  key: [
    {
      required: true,
      message: 'Please enter a key',
      trigger: ['blur', 'input'],
    },
  ],
  description: [
    {
      required: true,
      message: 'Please enter a short description',
      trigger: ['blur', 'input'],
    },
  ],
  content: [
    {
      required: true,
      message: 'Please enter the memory content',
      trigger: ['blur', 'input'],
    },
  ],
};

const paginationConfig = usePaginationConfig(currentPage, pageSize, totalCount);

async function fetchMemories(): Promise<void> {
  loading.value = true;
  try {
    const query: FilterableSearchQuery = {
      text: searchQuery.value || '',
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: sortKey.value,
      sort_direction: sortDirection.value === 'asc' ? 'asc' : 'desc',
      similarity_threshold: 0.5,
      column_filters: { ...columnFilters.value },
    };

    const response = await searchOrganisationMemories({ body: query });
    if (!response.data) {
      throw new Error('Failed to search organisation memories');
    }

    memories.value = response.data.results;
    totalCount.value = response.data.result_count;
    filterOptions.value = response.data.filter_options as Record<string, (string | null)[]>;
  } catch (error) {
    message.error('Failed to load memories');
    logger.error('Error loading organisation memories:', error);
  } finally {
    loading.value = false;
  }
}

// A new search starts from the first page and drops any manual sort. The field
// debounces its own keystrokes and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1;
  sortActivated.value = false;
  await fetchMemories();
}

watch([currentPage, pageSize], () => {
  void fetchMemories();
});

watch(columnFilters, () => {
  currentPage.value = 1;
  void fetchMemories();
});

// Auto-open filter bar when filters are applied (e.g. via deep link)
watch(
  hasActiveFilters,
  active => {
    if (active && !showFilterBar.value) {
      showFilterBar.value = true;
    }
  },
  { immediate: true }
);

function handleSortChange(sorter: DataTableSortState | DataTableSortState[] | null): void {
  if (!sorter || Array.isArray(sorter)) return;

  if (sorter.order === false) {
    sortKey.value = 'created_at';
    sortDirection.value = 'desc';
    sortActivated.value = false;
  } else {
    sortActivated.value = true;
    sortKey.value = String(sorter.columnKey);
    sortDirection.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  }
  currentPage.value = 1;
  void fetchMemories();
}

function handleRowClick(row: OrganisationMemoryInDb, e: MouseEvent): void {
  const target = e.target as HTMLElement;
  if (target.closest('button') || target.closest('a')) return;

  selectedMemory.value = row;
  showDetailModal.value = true;
}

function openCreate(): void {
  editingMemory.value = null;
  formData.value = emptyForm();
  showFormModal.value = true;
}

function openEdit(memory: OrganisationMemoryInDb): void {
  editingMemory.value = memory;
  formData.value = {
    key: memory.key,
    description: memory.description ?? '',
    content: memory.content,
    scope: memory.scope,
  };
  showDetailModal.value = false;
  showFormModal.value = true;
}

function handleSave(): void {
  void formRef.value?.validate(errors => {
    void (async (): Promise<void> => {
      if (errors) return;

      saving.value = true;
      try {
        if (editingMemory.value) {
          await updateOrganisationMemory({
            path: { uid: editingMemory.value.uid },
            body: formData.value,
          });
          message.success('Memory updated successfully');
        } else {
          await createOrganisationMemory({ body: formData.value });
          message.success('Memory created successfully');
        }
        showFormModal.value = false;
        await fetchMemories();
      } catch (error) {
        message.error(editingMemory.value ? 'Failed to update memory' : 'Failed to create memory');
        logger.error('Error saving organisation memory:', error);
      } finally {
        saving.value = false;
      }
    })();
  });
}

async function handleDelete(uid: string): Promise<void> {
  try {
    await deleteOrganisationMemory({ path: { uid } });
    message.success('Memory deleted successfully');
    await fetchMemories();
  } catch (error) {
    message.error('Failed to delete memory');
    logger.error('Error deleting organisation memory:', error);
  }
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString();
}

function sortOrderFor(key: string): 'ascend' | 'descend' | false {
  if (!sortActivated.value || sortKey.value !== key) return false;
  return sortDirection.value === 'asc' ? 'ascend' : 'descend';
}

const columns = computed((): DataTableColumns<OrganisationMemoryInDb> => [
  {
    title: 'Key',
    key: 'key',
    width: 200,
    sorter: true,
    sortOrder: sortOrderFor('key'),
    ellipsis: {
      tooltip: true,
    },
  },
  {
    title: 'Description',
    key: 'description',
    width: 300,
    ellipsis: {
      tooltip: true,
    },
    render: (row: OrganisationMemoryInDb): string => row.description ?? '—',
  },
  {
    title: 'Scope',
    key: 'scope',
    width: 180,
    sorter: true,
    sortOrder: sortOrderFor('scope'),
    render: (row: OrganisationMemoryInDb): VNode =>
      h(NTag, { size: 'small' }, { default: () => scopeLabels[row.scope] }),
  },
  {
    title: 'Created',
    key: 'created_at',
    width: 120,
    sorter: true,
    sortOrder: sortOrderFor('created_at'),
    render: (row: OrganisationMemoryInDb): string => formatDate(row.created_at),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 100,
    render: (row: OrganisationMemoryInDb): VNode => {
      return h(
        NSpace,
        {
          align: 'center',
          justify: 'center',
          size: [8, 0],
          inline: true,
          wrap: false,
        },
        {
          default: () => [
            h(
              NButton,
              {
                quaternary: true,
                size: 'small',
                onClick: () => {
                  openEdit(row);
                },
              },
              { icon: () => h(NIcon, null, { default: () => h(IconEdit) }) }
            ),
            h(
              NPopconfirm,
              {
                onPositiveClick: () => handleDelete(row.uid),
              },
              {
                default: () => 'Are you sure you want to delete this memory?',
                trigger: () =>
                  h(
                    NButton,
                    {
                      quaternary: true,
                      size: 'small',
                      type: 'error',
                    },
                    { icon: () => h(NIcon, null, { default: () => h(IconDelete) }) }
                  ),
              }
            ),
          ],
        }
      );
    },
  },
]);

onMounted(async (): Promise<void> => {
  await fetchMemories();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.memories }}</n-breadcrumb-item>
    </template>

    <n-flex justify="space-between" align="center" style="margin-bottom: 16px">
      <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
        {{ navLabels.memories }}
      </gaia-header>

      <n-space align="center" :wrap="false">
        <n-button
          :type="hasActiveFilters ? 'primary' : 'default'"
          :quaternary="!hasActiveFilters"
          @click="showFilterBar = !showFilterBar"
        >
          <template #icon>
            <n-icon><funnel-outline /></n-icon>
          </template>
          Filter
        </n-button>
        <gaia-search-input v-model:value="searchQuery" :debounce="500" @search="applySearch" />
        <n-button type="primary" @click="openCreate">
          <template #icon>
            <n-icon><plus-outlined /></n-icon>
          </template>
          Add Memory
        </n-button>
      </n-space>
    </n-flex>

    <n-alert type="info" style="margin-bottom: 16px">
      <template #header>
        <strong>How Memories Work</strong>
      </template>
      <p>
        Memories are knowledge the agents draw on when performing tasks for your organisation. Use
        them to set policies the agents should follow — for example, which jurisdiction to consider
        for their tasks.
      </p>
      <p style="margin-top: 8px">
        Each memory has a scope that decides which agent surface it applies to (Review, Playbooks,
        Contract Management, or Chat). Memories with the General scope apply everywhere.
      </p>
    </n-alert>

    <filter-bar
      v-model:visible="showFilterBar"
      :filter-options="filterOptions"
      :active-filters="columnFilters"
      :filter-config="filterConfig"
      :value-label-map="{ scope: scopeLabels }"
      @update:filters="setColumnFilters"
    />

    <gaia-table
      :columns="columns"
      :data="memories"
      :loading="loading"
      :remote="true"
      :pagination="paginationConfig"
      :row-key="(row: OrganisationMemoryInDb) => row.uid"
      :row-props="
        (row: OrganisationMemoryInDb) => ({
          onClick: (e: MouseEvent) => handleRowClick(row, e),
          style: { cursor: 'pointer' },
        })
      "
      @update:sorter="handleSortChange"
    />

    <!-- Detail Modal -->
    <n-modal
      v-model:show="showDetailModal"
      preset="card"
      :title="selectedMemory?.key"
      style="width: 720px"
    >
      <n-space v-if="selectedMemory" vertical size="large">
        <n-space align="center">
          <n-tag size="small">{{ scopeLabels[selectedMemory.scope] }}</n-tag>
          <n-text depth="3">{{ selectedMemory.description }}</n-text>
        </n-space>
        <markdown-renderer :content="selectedMemory.content" />
      </n-space>

      <template #footer>
        <n-space justify="end">
          <n-button @click="showDetailModal = false">Close</n-button>
          <n-button type="primary" @click="selectedMemory !== null && openEdit(selectedMemory)">
            Edit
          </n-button>
        </n-space>
      </template>
    </n-modal>

    <!-- Create/Edit Modal -->
    <n-modal
      v-model:show="showFormModal"
      preset="card"
      :title="editingMemory ? 'Edit Memory' : 'Add Memory'"
      style="width: 600px"
    >
      <n-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        label-placement="left"
        label-width="120"
      >
        <n-form-item label="Key" path="key" required>
          <gaia-input v-model:value="formData.key" placeholder="Enter a unique key" />
        </n-form-item>
        <n-form-item label="Description" path="description" required>
          <gaia-input
            v-model:value="formData.description"
            placeholder="One-line summary of the memory"
          />
        </n-form-item>
        <n-form-item label="Scope" path="scope">
          <gaia-select v-model:value="formData.scope" :options="scopeOptions" />
        </n-form-item>
        <n-form-item label="Content" path="content" required>
          <gaia-textarea
            v-model:value="formData.content"
            placeholder="Full content of the memory (markdown supported)"
            :autosize="{ minRows: 6, maxRows: 14 }"
          />
        </n-form-item>
      </n-form>

      <template #footer>
        <n-space justify="end">
          <n-button @click="showFormModal = false">Cancel</n-button>
          <n-button type="primary" :loading="saving" @click="handleSave">
            {{ editingMemory ? 'Update' : 'Create' }}
          </n-button>
        </n-space>
      </template>
    </n-modal>
  </Layout>
</template>
